import React, { FC, useMemo } from "react";
import { Image, View } from "react-native";
import { useI18nMessage } from "@lookiero/i18n-react";
import { Country } from "@lookiero/sty-psp-locale";
import { Text } from "@lookiero/sty-psp-ui";
import { Column, Row, useScreenSize } from "@lookiero/sty-psp-ui";
import { CheckoutItemProjection } from "../../../../../../projection/checkoutItem/checkoutItem";
import { size } from "../../../../../../projection/size/size";
import { useMediaImage } from "../../../../hooks/useMediaImage";
import { DOMAIN, I18nMessages } from "../../../../i18n/i18n";
import { Price } from "../price/Price";
import { IMAGE_SIZE, style as productVariantPreviewStyle } from "./ProductVariantPreview.style";

interface ProductVariantPreviewProps {
  readonly item: CheckoutItemProjection;
  readonly country: Country;
}

const ProductVariantPreview: FC<ProductVariantPreviewProps> = ({ item, country }) => {
  const cdnImageUrl = useMediaImage();
  const screenSize = useScreenSize();

  const sizeText = useI18nMessage({ domain: DOMAIN, id: I18nMessages.ITEM_SIZE });
  const uniqueText = useI18nMessage({ domain: DOMAIN, id: I18nMessages.ITEM_UNIQUE });

  const {
    productVariant: { media, brand, name, size: sizeProjection },
    price,
  } = item;

  const mainImage = media.find((mediaElement) => mediaElement.perspective === "MAIN");

  const style = useMemo(() => productVariantPreviewStyle(), []);

  return (
    <View style={[style.wrapper, screenSize === "S" && style.mobileWrapper]} testID="product-variant-preview">
      <Row style={style.row}>
        <Column size={{ M: "2/3", L: "2/3" }} style={style.content}>
          {mainImage && (
            <Image
              resizeMode="stretch"
              style={style.image}
              testID="product-variant-image"
              source={{
                uri: cdnImageUrl({
                  url: mainImage.url,
                  width: IMAGE_SIZE,
                }),
              }}
            />
          )}

          <View style={style.info}>
            <Text level={2} style={style.text} variant="detail">
              {brand}
            </Text>
            <Text level={2} variant="detail">
              {name}
            </Text>
            <Text level={2} style={style.text} variant="detail">
              {`${sizeText} ${sizeProjection.unique ? uniqueText : size({ size: sizeProjection, country })}`}
            </Text>
          </View>

          <View style={style.price}>
            <Price
              amount={price.amount}
              currency={price.currency}
              discountedPrice={price.discountedPrice}
              withPercentage
            />
          </View>
        </Column>
      </Row>
    </View>
  );
};

export { ProductVariantPreview };
